import { APP_BASE_HREF, CommonModule } from '@angular/common';
import { BrowserModule, bootstrapApplication } from '@angular/platform-browser';
import { CookieService } from 'ngx-cookie-service';
import { devTools } from '@ngneat/elf-devtools';
import { enableProdMode, ErrorHandler, importProvidersFrom } from '@angular/core';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withFetch,
  withInterceptorsFromDi
} from '@angular/common/http';
import { JwtModule } from '@auth0/angular-jwt';
import { MAT_DIALOG_DEFAULT_OPTIONS, MatDialogConfig } from '@angular/material/dialog';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MAT_SNACK_BAR_DEFAULT_OPTIONS, MatSnackBarModule } from '@angular/material/snack-bar';
import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';

import { BaseInterceptor } from '@app/interceptor/base.interceptor';
import { ImpersonationInterceptor } from '@app/interceptor/impersonation.interceptor';
import { environment } from '@env/environment';
import { FlowDialogsModule } from '@app/shared/components/dialogs/flow-dialogs.module';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FourOhFourModule } from '@app/route/four-oh-four';
import { GoogleAnalyticsService } from '@app/shared/services/vendor';
import { RollbarErrorHandler, RollbarService, rollbarFactory } from '@state/rollbar';
import { VariableService } from '@app/shared/services';
import { ZendeskModule, ZendeskConfig } from '@state/zendesk';

import { DocumentsAppComponent } from './app/documents-app.component';
import { DocumentsAppRoutingModule } from './app/documents-app-routing.module';

function getCookie(name: string): string {
  let decodedCookie = decodeURIComponent(document.cookie);
  let cookieSegments = decodedCookie.split(/;/);

  for (let cookieSegment of cookieSegments) {
    let [cookieName, cookieValue] = cookieSegment.trim().split('=');
    if (cookieName === name) return cookieValue;
  }

  return '';
}

// Production toggles
if (environment.production) {
  enableProdMode();
}

// Initialize the app
let bootstrap = () =>
  bootstrapApplication(DocumentsAppComponent, {
    providers: [
      importProvidersFrom(
        BrowserModule,
        CommonModule,
        DocumentsAppRoutingModule,
        FlowDialogsModule,
        FormsModule,
        FourOhFourModule,
        MatSnackBarModule,
        // JWT will technically intercept HTTP calls, but we can do multiple interceptors
        JwtModule.forRoot({
          config: {
            allowedDomains: environment.JWT_WHITELIST_DOMAINS,
            tokenGetter: () => getCookie('token')
          }
        }),
        ReactiveFormsModule,
        ZendeskModule.forRoot(ZendeskConfig)
      ),
      CookieService,
      VariableService,
      GoogleAnalyticsService,
      { provide: APP_BASE_HREF, useValue: environment.docsUrl || '/' },
      { multi: true, provide: HTTP_INTERCEPTORS, useClass: BaseInterceptor },
      { multi: true, provide: HTTP_INTERCEPTORS, useClass: ImpersonationInterceptor },
      {
        provide: ErrorHandler,
        useClass: environment.runtimeEnv === 'development' ? ErrorHandler : RollbarErrorHandler
      },
      { provide: OVERLAY_DEFAULT_CONFIG, useValue: { usePopover: false } },
      { provide: MAT_DIALOG_DEFAULT_OPTIONS, useValue: { ...new MatDialogConfig(), disableClose: true } },
      { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { subscriptSizing: 'dynamic' } },
      {
        provide: MAT_SNACK_BAR_DEFAULT_OPTIONS,
        useValue: { duration: 5000, horizontalPosition: 'right', verticalPosition: 'top' }
      },
      {
        provide: MAT_SNACK_BAR_DEFAULT_OPTIONS,
        useValue: { duration: 5000, horizontalPosition: 'right', verticalPosition: 'top' }
      },
      { provide: RollbarService, useFactory: rollbarFactory },
      provideHttpClient(withFetch(), withInterceptorsFromDi())
    ]
  });
bootstrap()
  .then(() => {
    if (!environment.production) devTools();
  })
  .catch((err) => console.error(err));
